import React, { Fragment, Suspense, useEffect } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { messages } from '../../messages';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerContentWarningAndBrandSection } from '../ai-answer-footer/ai-answer-footer';
import { onAIAnswerLoadingDialogShown } from '../analytics';
import { Collapse } from '../smart-answer-card/collapse';
import { NLPSearchResultFormat } from '../types';

import { AIAnswerLoadingStream } from './ai-answer-loading-stream';
import GeneratingAnimation from './generating-animation';
import { AIAnswerSearchLoadingBorder, GeneratingContainer, GeneratingText } from './styled';

const styles = cssMap({
	scroll: {
		maxHeight: '150px',
		paddingTop: token('space.200'),
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarColor: `${token('color.border.bold')} transparent`,
	},
});

type AIAnswerLoadingDialogProps = {
	collapsed?: boolean;
	toggleCollapsedState?: () => void;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

// Used in quicksearch/advanced search
export const AIAnswerLoadingDialog = ({
	collapsed,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps) => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<LegacySmartAnswerLoadingDialog
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			callbacks={callbacks}
		/>
	);
};

const LegacySmartAnswerLoadingDialog = ({
	collapsed = true,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps) => {
	const { partialResponse, queryReasoning, isReadingAids, onClose } = useAIAnswerContext();
	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	const isQuickSearch = Boolean(!isReadingAids && onClose);

	if (!partialResponse || partialResponse?.format !== NLPSearchResultFormat.MARKDOWN) {
		return (
			<AIAnswerSearchLoadingBorder isStreaming={false}>
				<GeneratingContainer>
					<GeneratingAnimation />
					<GeneratingText>
						{serveSAINReasoningExperiment && queryReasoning ? (
							queryReasoning
						) : (
							<FormattedMessage {...messages.ai_answer_generating_rovo} />
						)}
					</GeneratingText>
				</GeneratingContainer>
				<AIAnswerContentWarningAndBrandSection />
			</AIAnswerSearchLoadingBorder>
		);
	}

	if (isQuickSearch) {
		return (
			<AIAnswerSearchLoadingBorder isStreaming>
				<Suspense fallback={<Fragment />}>
					<SmartCardProvider>
						<Box xcss={styles.scroll}>
							<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
						</Box>
						<AIAnswerContentWarningAndBrandSection />
					</SmartCardProvider>
				</Suspense>
			</AIAnswerSearchLoadingBorder>
		);
	}

	return (
		<AIAnswerSearchLoadingBorder isStreaming>
			<Suspense fallback={<Fragment />}>
				<SmartCardProvider>
					<Collapse
						collapsed={collapsed}
						isLoading
						isStreaming
						expandWhenClickedAnywhere
						toggleCollapsedState={toggleCollapsedState}
						callbacks={callbacks}
					>
						<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
					</Collapse>
					<AIAnswerContentWarningAndBrandSection />
				</SmartCardProvider>
			</Suspense>
		</AIAnswerSearchLoadingBorder>
	);
};
